export function copyBytes(bytes: ArrayBuffer | ArrayBufferView): Uint8Array {
  const source =
    Object.prototype.toString.call(bytes) === '[object ArrayBuffer]'
      ? new Uint8Array(bytes as ArrayBuffer)
      : new Uint8Array(
          (bytes as ArrayBufferView).buffer,
          (bytes as ArrayBufferView).byteOffset,
          (bytes as ArrayBufferView).byteLength,
        );
  const copy = new Uint8Array(source.byteLength);
  copy.set(source);
  return copy;
}

export const PREVIEW_TEXT_MAX_CHARS = 1_000_000;
export const PREVIEW_TABLE_MAX_ROWS = 200;
export const PREVIEW_TABLE_MAX_COLS = 40;

export interface PreviewTable {
  rows: string[][];
  truncated: boolean;
  /** Excel row number for each displayed row. Absent for CSV. */
  excelRows?: number[];
}

/** Safe to drop into a <pre> via innerHTML. Whitespace and line breaks stay. */
export function escapePreviewHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function decodePreviewText(bytes: ArrayBuffer | ArrayBufferView): {
  text: string;
  truncated: boolean;
} {
  const view = copyBytes(bytes);

  let encoding = 'utf-8';
  let start = 0;
  if (view.length >= 3 && view[0] === 0xef && view[1] === 0xbb && view[2] === 0xbf) {
    start = 3;
  } else if (view.length >= 2 && view[0] === 0xff && view[1] === 0xfe) {
    encoding = 'utf-16le';
    start = 2;
  } else if (view.length >= 2 && view[0] === 0xfe && view[1] === 0xff) {
    encoding = 'utf-16be';
    start = 2;
  } else {
    try {
      new TextDecoder('utf-8', { fatal: true }).decode(view);
    } catch {
      encoding = 'windows-1252';
    }
  }

  const text = new TextDecoder(encoding)
    .decode(view.subarray(start))
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');
  if (text.length <= PREVIEW_TEXT_MAX_CHARS) return { text, truncated: false };
  return { text: text.slice(0, PREVIEW_TEXT_MAX_CHARS), truncated: true };
}

function detectDelimiter(sample: string): ',' | ';' | '\t' {
  const lines = sample
    .split('\n')
    .filter((line) => line.trim().length > 0)
    .slice(0, 8);
  const score = (delimiter: string) =>
    lines.reduce((total, line) => total + Math.max(0, line.split(delimiter).length - 1), 0);
  const comma = score(',');
  const semi = score(';');
  const tab = score('\t');
  if (semi >= comma && semi >= tab && semi > 0) return ';';
  if (tab >= comma && tab > 0) return '\t';
  return ',';
}

export function parseDelimitedTable(text: string, delimiter?: ',' | ';' | '\t'): PreviewTable {
  const chosen = delimiter ?? detectDelimiter(text);
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  let truncated = false;

  const pushCell = () => {
    if (row.length < PREVIEW_TABLE_MAX_COLS) row.push(cell);
    else truncated = true;
    cell = '';
  };
  const pushRow = () => {
    if (rows.length < PREVIEW_TABLE_MAX_ROWS) rows.push(row);
    else truncated = true;
    row = [];
  };

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i] ?? '';
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cell += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      continue;
    }
    if (ch === chosen) {
      pushCell();
      continue;
    }
    if (ch === '\n') {
      pushCell();
      pushRow();
      continue;
    }
    cell += ch;
  }
  if (cell.length > 0 || row.length > 0) {
    pushCell();
    pushRow();
  }

  while (rows.length > 0 && rows[rows.length - 1]?.every((value) => value.trim() === '')) {
    rows.pop();
  }
  return { rows, truncated };
}
